(function (root, factory) {
  var api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (root) root.EpicImageEnhancementCore = api;
})(typeof window !== 'undefined' ? window : globalThis, function () {
  'use strict';

  /*
   * Pure rules for the "how should we print your image?" question on custom
   * upload products. The server reads the same strings in
   * core/image_enhancement.py; tests/test_image_enhancement.py fails when the
   * two drift. A value the server does not recognise reads as "keep", so a
   * wording change here can never turn a customer's keep into an AI upscale.
   */
  var PROPERTY_NAME = 'Image enhancement';
  var VALUE_ENHANCE = 'Enhance my image';
  var VALUE_KEEP = 'Keep exactly as uploaded';
  var RESOLUTION_PROPERTY = '_image_print_resolution';
  // Ask when the upscaler would send the image to its generative engine
  // (long edge at most 1300 px) or when it prints below 72 DPI. A 12 MP phone
  // photo on the 100 cm mat (about 85 DPI) is not asked.
  var GENERATIVE_MAX_LONG_EDGE = 1300;
  var ASK_BELOW_DPI = 72;

  // Same thresholds as skills/Image_Upscaler/upscale_engine.py.
  var PIXEL_ART_MAX_LONG_EDGE = 2048;
  var PIXEL_ART_MAX_COLORS = 256;
  var PIXEL_ART_MIN_FLAT_NEIGHBOURS = 0.6;

  var CUSTOM_PREFIXES = ['CUS', 'CUSTOMRGB', 'ESPORT'];
  var MM_PER_INCH = 25.4;

  function isCustomUploadSku(sku) {
    var upper = String(sku || '').trim().toUpperCase();
    for (var index = 0; index < CUSTOM_PREFIXES.length; index += 1) {
      if (upper.indexOf(CUSTOM_PREFIXES[index]) === 0) return true;
    }
    return false;
  }

  /* CUS-900x400x4 -> { widthMm: 900, heightMm: 400 }, like parse_sku_dimensions. */
  function skuDimensions(sku) {
    if (!isCustomUploadSku(sku)) return null;
    var text = String(sku).trim();
    var dash = text.lastIndexOf('-');
    if (dash === -1) return null;
    var parts = text.slice(dash + 1).toLowerCase().split('x');
    if (parts.length < 2) return null;
    var width = parseInt(parts[0], 10);
    var height = parseInt(parts[1], 10);
    if (!(width > 0 && height > 0) || String(width) !== parts[0] || String(height) !== parts[1]) return null;
    return { widthMm: width, heightMm: height };
  }

  /* The lower of the two axes' pixels per inch at the ordered size. */
  function printDpi(pixelWidth, pixelHeight, dims) {
    var w = Number(pixelWidth);
    var h = Number(pixelHeight);
    if (!(w > 0 && h > 0) || !dims) return null;
    // An upload may be rotated against the mat; compare long edge to long edge.
    var longPx = Math.max(w, h);
    var shortPx = Math.min(w, h);
    var longIn = Math.max(dims.widthMm, dims.heightMm) / MM_PER_INCH;
    var shortIn = Math.min(dims.widthMm, dims.heightMm) / MM_PER_INCH;
    return Math.min(longPx / longIn, shortPx / shortIn);
  }

  /* Ask for a small or low-resolution image, or when it cannot be measured. */
  function needsChoice(dpi, longEdgePx) {
    if (dpi === null || dpi === undefined || !isFinite(dpi)) return true;
    if (Number(longEdgePx) > 0 && Number(longEdgePx) <= GENERATIVE_MAX_LONG_EDGE) return true;
    return dpi < ASK_BELOW_DPI;
  }

  function resolutionRecord(pixelWidth, pixelHeight, dims, dpi) {
    if (!(pixelWidth > 0 && pixelHeight > 0) || !dims) return 'not measured';
    return (
      Math.round(pixelWidth) + 'x' + Math.round(pixelHeight) + 'px; ' +
      (dpi ? Math.round(dpi) : '?') + ' DPI at ' +
      dims.widthMm / 10 + 'x' + dims.heightMm / 10 + 'cm'
    );
  }

  /*
   * Pixel art: a small image of few exact colours in flat runs. `data` is
   * RGBA bytes (ImageData.data). Same rule as the server's plain scale, so
   * the hint here matches what the supplier's plain scale will do.
   */
  function looksLikePixelArt(data, width, height) {
    if (!(width > 0 && height > 0) || Math.max(width, height) > PIXEL_ART_MAX_LONG_EDGE) return false;
    if (!data || data.length < width * height * 4) return false;
    var colours = {};
    var count = 0;
    var equal = 0;
    var total = 0;
    for (var y = 0; y < height; y += 1) {
      for (var x = 0; x < width; x += 1) {
        var i = (y * width + x) * 4;
        var key = data[i] + ',' + data[i + 1] + ',' + data[i + 2] + ',' + data[i + 3];
        if (!colours[key]) {
          colours[key] = true;
          count += 1;
          if (count > PIXEL_ART_MAX_COLORS) return false;
        }
        if (x + 1 < width) {
          total += 1;
          if (samePixel(data, i, i + 4)) equal += 1;
        }
        if (y + 1 < height) {
          total += 1;
          if (samePixel(data, i, i + width * 4)) equal += 1;
        }
      }
    }
    return total > 0 && equal / total >= PIXEL_ART_MIN_FLAT_NEIGHBOURS;
  }

  function samePixel(data, a, b) {
    return data[a] === data[b] && data[a + 1] === data[b + 1] &&
      data[a + 2] === data[b + 2] && data[a + 3] === data[b + 3];
  }

  function propertyValue(choice) {
    if (choice === 'enhance') return VALUE_ENHANCE;
    if (choice === 'keep') return VALUE_KEEP;
    return '';
  }

  /*
   * Upload-Lift serves uploads from upload.cloudlift.app, which sends no CORS
   * header; its own script reads pixels from the storage bucket behind it.
   */
  function corsUrl(url) {
    return String(url || '')
      .replace('https://upload.dev.cloudlift.app/s/dev', 'https://storage.googleapis.com/cloudlift-app-cloud-dev-upload')
      .replace('https://upload.cloudlift.app/s', 'https://storage.googleapis.com/cloudlift-app-cloud-prod-upload');
  }

  return {
    PROPERTY_NAME: PROPERTY_NAME,
    VALUE_ENHANCE: VALUE_ENHANCE,
    VALUE_KEEP: VALUE_KEEP,
    RESOLUTION_PROPERTY: RESOLUTION_PROPERTY,
    ASK_BELOW_DPI: ASK_BELOW_DPI,
    GENERATIVE_MAX_LONG_EDGE: GENERATIVE_MAX_LONG_EDGE,
    PIXEL_ART_MAX_LONG_EDGE: PIXEL_ART_MAX_LONG_EDGE,
    PIXEL_ART_MAX_COLORS: PIXEL_ART_MAX_COLORS,
    PIXEL_ART_MIN_FLAT_NEIGHBOURS: PIXEL_ART_MIN_FLAT_NEIGHBOURS,
    isCustomUploadSku: isCustomUploadSku,
    skuDimensions: skuDimensions,
    printDpi: printDpi,
    needsChoice: needsChoice,
    resolutionRecord: resolutionRecord,
    looksLikePixelArt: looksLikePixelArt,
    propertyValue: propertyValue,
    corsUrl: corsUrl
  };
});
